import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import {
    OUTBOUND_MULTIMEDIA_ACCEPTED_TYPES,
    OUTBOUND_MULTIMEDIA_MAX_SIZE_BYTES,
} from 'src/views/OmniChannel/constants/outboundMultimedia';

export type TOutboundMultimediaDropzoneHandle = {
    open: () => void;
};

type TOutboundMultimediaDropzoneProps = {
    disabled?: boolean;
    onFilesSelected: (files: File[]) => void;
};

export const OutboundMultimediaDropzone = forwardRef<
    TOutboundMultimediaDropzoneHandle,
    TOutboundMultimediaDropzoneProps
>(({ disabled = false, onFilesSelected }, ref) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const handleDrop = useCallback(
        (acceptedFiles: File[], rejectedFiles: File[]) => {
            if (rejectedFiles.length > 0) {
                enqueueSnackbar(t('Errors.SizeErrorMessage'), {
                    variant: 'error',
                });
                return;
            }

            if (acceptedFiles.length > 0) {
                onFilesSelected(acceptedFiles);
            }
        },
        [enqueueSnackbar, onFilesSelected, t]
    );

    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop: handleDrop,
        accept: OUTBOUND_MULTIMEDIA_ACCEPTED_TYPES,
        minSize: 0,
        maxSize: OUTBOUND_MULTIMEDIA_MAX_SIZE_BYTES,
        disabled,
        noClick: true,
        noKeyboard: true,
        multiple: true,
    });

    useImperativeHandle(ref, () => ({ open }), [open]);

    return (
        <div {...getRootProps()} style={{ display: 'none' }}>
            <input {...getInputProps()} />
        </div>
    );
});

OutboundMultimediaDropzone.displayName = 'OutboundMultimediaDropzone';
